/**
 * Activity Log API
 * 
 * Provides functions for logging and retrieving activity across the entire app.
 * Uses the unified activity_log table in Supabase.
 */

import { supabase } from './supabase';

// =====================================================
// TYPES
// =====================================================

export type EntityType = 
  | 'assignment'
  | 'vehicle'
  | 'driver'
  | 'delivery_form'
  | 'receiving_form'
  | 'job_card'
  | 'salik'
  | 'accounting'
  | 'partnership'
  | 'user'
  | 'system';

export type ActionType =
  | 'create'
  | 'update'
  | 'delete'
  | 'status_change'
  | 'assignment'
  | 'unassignment'
  | 'form_submission'
  | 'bulk_import'
  | 'login'
  | 'logout'
  | 'export'
  | 'print';

export interface ActivityLogEntry {
  id: string;
  entity_type: EntityType;
  entity_id: string;
  action_type: ActionType;
  changes?: Record<string, { before: any; after: any }>;
  description: string;
  user_id?: string;
  user_name: string;
  user_email?: string;
  user_role?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface RecentActivity extends ActivityLogEntry {
  entity_name: string | null;
}

export interface ActivitySummaryByEntity {
  entity_type: EntityType;
  total_actions: number;
  unique_users: number;
  unique_entities: number;
  first_activity: string;
  last_activity: string;
}

export interface ActivitySummaryByUser {
  user_id: string;
  user_name: string;
  user_email: string;
  total_actions: number;
  last_activity: string;
}

export interface LogActivityParams {
  entity_type: EntityType;
  entity_id: string;
  action_type: ActionType;
  changes?: Record<string, { before: any; after: any }>;
  description: string;
  user_name?: string;
  user_email?: string;
  user_role?: string;
}

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Get current user info from authStore
 */
async function getCurrentUserInfo() {
  try {
    // Import authStore dynamically to avoid circular dependencies
    const { useAuthStore } = await import('../stores/authStore');
    const user = useAuthStore.getState().user;
    
    if (!user) {
      return {
        user_id: null,
        user_name: 'System',
        user_email: null,
        user_role: 'system',
      };
    }

    return {
      user_id: user.id,
      user_name: user.name || user.email?.split('@')[0] || 'Unknown User',
      user_email: user.email || null,
      user_role: user.role || 'user',
    };
  } catch (error) {
    console.error('Error getting current user info:', error);
    return {
      user_id: null,
      user_name: 'System',
      user_email: null,
      user_role: 'system',
    };
  }
}

/**
 * Get client IP address (simplified for web - won't work on mobile)
 */
async function getClientIP(): Promise<string | null> {
  // This is a placeholder - in production you'd use a service like ipify
  // For now, we'll return null and let the database handle it
  return null;
}

/**
 * Get user agent string
 */
function getUserAgent(): string | null {
  if (typeof navigator !== 'undefined') {
    return navigator.userAgent;
  }
  return null;
}

// =====================================================
// CORE API FUNCTIONS
// =====================================================

/**
 * Log an activity to the unified activity_log table
 * 
 * @example
 * ```typescript
 * await logActivity({
 *   entity_type: 'assignment',
 *   entity_id: assignmentId,
 *   action_type: 'create',
 *   changes: {
 *     status: { before: null, after: 'active' },
 *     vehicle_id: { before: null, after: vehicleId }
 *   },
 *   description: 'Created new assignment for Vehicle ABC123'
 * });
 * ```
 */
export async function logActivity(params: LogActivityParams): Promise<{ success: boolean; error?: string; id?: string }> {
  try {
    const userInfo = await getCurrentUserInfo();
    const ip_address = await getClientIP();
    const user_agent = getUserAgent();

    const { data, error } = await supabase.rpc('log_activity', {
      p_entity_type: params.entity_type,
      p_entity_id: params.entity_id,
      p_action_type: params.action_type,
      p_changes: params.changes || null,
      p_description: params.description,
      p_user_id: userInfo.user_id,
      p_user_name: params.user_name || userInfo.user_name,
      p_user_email: params.user_email || userInfo.user_email,
      p_user_role: params.user_role || userInfo.user_role,
      p_ip_address: ip_address,
      p_user_agent: user_agent,
    });

    if (error) {
      console.error('Error logging activity:', error);
      return { success: false, error: error.message };
    }

    return { success: true, id: data };
  } catch (error: any) {
    console.error('Error logging activity:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get all activity for a specific entity
 * 
 * @example
 * ```typescript
 * const logs = await getEntityActivity('assignment', assignmentId);
 * ```
 */
export async function getEntityActivity(
  entity_type: EntityType,
  entity_id: string,
  limit: number = 100
): Promise<{ data: ActivityLogEntry[] | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('activity_log')
      .select('*')
      .eq('entity_type', entity_type)
      .eq('entity_id', entity_id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching entity activity:', error);
      return { data: null, error: error.message };
    }

    return { data: data as ActivityLogEntry[], error: null };
  } catch (error: any) {
    console.error('Error fetching entity activity:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get recent activity across all entities
 * 
 * @example
 * ```typescript
 * const logs = await getRecentActivity(50);
 * ```
 */
export async function getRecentActivity(limit: number = 100): Promise<{ data: RecentActivity[] | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('recent_activity')
      .select('*')
      .limit(limit);

    if (error) {
      console.error('Error fetching recent activity:', error);
      return { data: null, error: error.message };
    }

    return { data: data as RecentActivity[], error: null };
  } catch (error: any) {
    console.error('Error fetching recent activity:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get all activity by a specific user
 * 
 * @example
 * ```typescript
 * const logs = await getUserActivity(userId);
 * ```
 */
export async function getUserActivity(
  user_id: string,
  limit: number = 100
): Promise<{ data: ActivityLogEntry[] | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('activity_log')
      .select('*')
      .eq('user_id', user_id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching user activity:', error);
      return { data: null, error: error.message };
    }

    return { data: data as ActivityLogEntry[], error: null };
  } catch (error: any) {
    console.error('Error fetching user activity:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get activity summary by entity type
 */
export async function getActivitySummaryByEntity(): Promise<{ data: ActivitySummaryByEntity[] | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('activity_summary_by_entity')
      .select('*');

    if (error) {
      console.error('Error fetching activity summary by entity:', error);
      return { data: null, error: error.message };
    }

    return { data: data as ActivitySummaryByEntity[], error: null };
  } catch (error: any) {
    console.error('Error fetching activity summary by entity:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get activity summary by user
 */
export async function getActivitySummaryByUser(): Promise<{ data: ActivitySummaryByUser[] | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('activity_summary_by_user')
      .select('*');

    if (error) {
      console.error('Error fetching activity summary by user:', error);
      return { data: null, error: error.message };
    }

    return { data: data as ActivitySummaryByUser[], error: null };
  } catch (error: any) {
    console.error('Error fetching activity summary by user:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Search activity logs by action type
 */
export async function getActivityByActionType(
  action_type: ActionType,
  limit: number = 100
): Promise<{ data: ActivityLogEntry[] | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('activity_log')
      .select('*')
      .eq('action_type', action_type)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching activity by action type:', error);
      return { data: null, error: error.message };
    }

    return { data: data as ActivityLogEntry[], error: null };
  } catch (error: any) {
    console.error('Error fetching activity by action type:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Search for specific field changes in activity logs
 * 
 * @example
 * ```typescript
 * // Find all status changes to 'completed'
 * const logs = await searchActivityChanges({ status: { after: 'completed' } });
 * ```
 */
export async function searchActivityChanges(
  changes: Record<string, any>,
  limit: number = 100
): Promise<{ data: ActivityLogEntry[] | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('activity_log')
      .select('*')
      .contains('changes', changes)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error searching activity changes:', error);
      return { data: null, error: error.message };
    }

    return { data: data as ActivityLogEntry[], error: null };
  } catch (error: any) {
    console.error('Error searching activity changes:', error);
    return { data: null, error: error.message };
  }
}

// =====================================================
// CONVENIENCE FUNCTIONS FOR COMMON OPERATIONS
// =====================================================

/**
 * Log assignment creation
 */
export async function logAssignmentCreation(params: {
  assignment_id: string;
  vehicle_id: string;
  driver_id: string;
  secondary_driver_id?: string;
  monthly_rate?: number;
  daily_rate?: number;
  status: string;
}) {
  return logActivity({
    entity_type: 'assignment',
    entity_id: params.assignment_id,
    action_type: 'create',
    changes: {
      status: { before: null, after: params.status },
      vehicle_id: { before: null, after: params.vehicle_id },
      primary_driver_id: { before: null, after: params.driver_id },
      ...(params.secondary_driver_id && {
        secondary_driver_id: { before: null, after: params.secondary_driver_id },
      }),
      ...(params.monthly_rate && {
        monthly_rate: { before: null, after: params.monthly_rate },
      }),
      ...(params.daily_rate && {
        daily_rate: { before: null, after: params.daily_rate },
      }),
    },
    description: `Created new assignment${params.secondary_driver_id ? ' (partnership)' : ''}`,
  });
}

/**
 * Log assignment update
 */
export async function logAssignmentUpdate(params: {
  assignment_id: string;
  changes: Record<string, { before: any; after: any }>;
  description?: string;
}) {
  return logActivity({
    entity_type: 'assignment',
    entity_id: params.assignment_id,
    action_type: 'update',
    changes: params.changes,
    description: params.description || 'Updated assignment',
  });
}

/**
 * Log assignment status change
 */
export async function logAssignmentStatusChange(params: {
  assignment_id: string;
  old_status: string;
  new_status: string;
}) {
  return logActivity({
    entity_type: 'assignment',
    entity_id: params.assignment_id,
    action_type: 'status_change',
    changes: {
      status: { before: params.old_status, after: params.new_status },
    },
    description: `Assignment status changed from ${params.old_status} to ${params.new_status}`,
  });
}

/**
 * Log form submission
 */
export async function logFormSubmission(params: {
  form_id: string;
  form_type: 'delivery_form' | 'receiving_form';
  assignment_id: string;
  vehicle_id: string;
  driver_id: string;
  odometer?: number;
  fuel_level?: number;
}) {
  return logActivity({
    entity_type: params.form_type,
    entity_id: params.form_id,
    action_type: 'form_submission',
    changes: {
      assignment_id: { before: null, after: params.assignment_id },
      vehicle_id: { before: null, after: params.vehicle_id },
      driver_id: { before: null, after: params.driver_id },
      ...(params.odometer && {
        odometer: { before: null, after: params.odometer },
      }),
      ...(params.fuel_level && {
        fuel_level: { before: null, after: params.fuel_level },
      }),
    },
    description: `Submitted ${params.form_type === 'delivery_form' ? 'delivery' : 'receiving'} form`,
  });
}

/**
 * Log vehicle update
 */
export async function logVehicleUpdate(params: {
  vehicle_id: string;
  changes: Record<string, { before: any; after: any }>;
  description?: string;
}) {
  return logActivity({
    entity_type: 'vehicle',
    entity_id: params.vehicle_id,
    action_type: 'update',
    changes: params.changes,
    description: params.description || 'Updated vehicle',
  });
}

/**
 * Log driver update
 */
export async function logDriverUpdate(params: {
  driver_id: string;
  changes: Record<string, { before: any; after: any }>;
  description?: string;
}) {
  return logActivity({
    entity_type: 'driver',
    entity_id: params.driver_id,
    action_type: 'update',
    changes: params.changes,
    description: params.description || 'Updated driver',
  });
}

/**
 * Log job card creation
 */
export async function logJobCardCreation(params: {
  job_card_id: string;
  job_number: string;
  vehicle_plate: string;
  make_model: string;
  status: string;
}) {
  return logActivity({
    entity_type: 'job_card',
    entity_id: params.job_card_id,
    action_type: 'create',
    changes: {
      status: { before: null, after: params.status },
      job_number: { before: null, after: params.job_number },
      vehicle_plate: { before: null, after: params.vehicle_plate },
    },
    description: `Created job card ${params.job_number} for vehicle ${params.vehicle_plate} (${params.make_model})`,
  });
}

/**
 * Log job card completion
 */
export async function logJobCardCompletion(params: {
  job_card_id: string;
  job_number: string;
  vehicle_plate: string;
  old_status: string;
  return_date: string;
}) {
  return logActivity({
    entity_type: 'job_card',
    entity_id: params.job_card_id,
    action_type: 'status_change',
    changes: {
      status: { before: params.old_status, after: 'completed' },
      return_date: { before: null, after: params.return_date },
    },
    description: `Completed job card ${params.job_number} for vehicle ${params.vehicle_plate}`,
  });
}

/**
 * Log job card deletion
 */
export async function logJobCardDeletion(params: {
  job_card_id: string;
  job_number: string;
  vehicle_plate: string;
  make_model: string;
  status: string;
  request_date?: string;
}) {
  return logActivity({
    entity_type: 'job_card',
    entity_id: params.job_card_id,
    action_type: 'delete',
    changes: {
      status: { before: params.status, after: null },
      deleted: { before: false, after: true },
    },
    description: `Deleted job card ${params.job_number} for vehicle ${params.vehicle_plate} (${params.make_model})${params.request_date ? ` - Request Date: ${params.request_date}` : ''}`,
  });
}

/**
 * Log job card print
 */
export async function logJobCardPrint(params: {
  job_card_id: string;
  job_number: string;
  vehicle_plate: string;
}) {
  return logActivity({
    entity_type: 'job_card',
    entity_id: params.job_card_id,
    action_type: 'print',
    description: `Printed job card ${params.job_number} for vehicle ${params.vehicle_plate}`,
  });
}
